import React, {
  useState,
  useCallback,
  FunctionComponent,
  useEffect,
} from 'react';
import classNames from 'classnames';
import _ from 'underscore';
import { forNamespace } from 'app/common/lib/util/i18n';
import Banner from 'app/common/components/banner/banner';
import {
  PrimaryButton,
  ButtonSizes,
} from 'app/common/components/controls/button/button';
import styles from './AtlassianAutomaticMigrationBanner.less';
import { N0 } from '@trello/colors';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';
import { useAtlassianAccountOnboardingQuery } from '../AtlassianAccountOnboardingQuery.generated';
import { Member_LoginType } from '@trello/graphql/generated';
import {
  AtlassianAutomaticOnboardingOverlay,
  ScreenType,
} from '../AutomaticOnboarding/AtlassianAutomaticOnboardingOverlay';
import { needsAtlassianAccountRelinkedConfirmation } from './needsAtlassianAccountRelinkedConfirmation';
import { Analytics } from '@trello/atlassian-analytics';

const format = forNamespace(['aa onboarding', 'banner'], {
  shouldEscapeStrings: false,
});

const RightWaveImageUrl = require('resources/images/onboarding-banner/banner-wave-right.svg');
const LeftWaveImageUrl = require('resources/images/onboarding-banner/banner-wave-left.svg');

let analyticsContext = {};
let analyticsContainers = {};
const analyticsSource = 'atlassianAccountAutomaticOnboardingBanner';

const query = window.location.search.split('?q=').pop();

const shouldShowAutomaticMigrationConfirmationOverlay =
  query && decodeURIComponent(query).includes('auto_onboarding=success');

const shouldShowEmailMismatchOverlay =
  query &&
  decodeURIComponent(query).includes('auto_onboarding=emailMismatchError');

const shouldShowAtlassianProfile =
  query && decodeURIComponent(query).includes('show_atlassian_profile=true');

const shouldShowSuccessOverlay =
  query && decodeURIComponent(query).includes('onboarding=success');

export const forceShowOnboardingOverlay = (
  oneTimeMessagesDismissed: string[],
) =>
  shouldShowAutomaticMigrationConfirmationOverlay ||
  shouldShowEmailMismatchOverlay ||
  shouldShowAtlassianProfile ||
  shouldShowSuccessOverlay ||
  needsAtlassianAccountRelinkedConfirmation(oneTimeMessagesDismissed);

export const AtlassianAutomaticMigrationBanner: FunctionComponent = () => {
  const [sentInitialAnalytics, setSentInitalAnalytics] = useState(false);
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const [screen, setScreen] = useState<ScreenType>();

  const { data } = useAtlassianAccountOnboardingQuery({
    variables: { memberId: 'me' },
  });

  const me = data?.member;
  const name = me?.fullName;
  const id = me?.id;
  const email = me?.email;
  const aaEmail = me?.aaEmail;
  const aaId = me?.aaId;
  const aaLogin = me?.logins.find((l) => l.email === aaEmail);
  const aaLoginId = aaLogin?.id;
  const ent = me && !_.isEmpty(me.enterprises) ? me.enterprises[0] : undefined;
  const orgName = ent?.displayName;
  const credentialsRemovedCount = me?.credentialsRemovedCount;
  const oneTimeMessagesDismissed = me?.oneTimeMessagesDismissed || [];
  const isAaMastered = me?.isAaMastered;
  const isAtlassianLogin =
    me?.loginTypes && me.loginTypes.includes(Member_LoginType.Atlassian);

  // Return null if any of the follow fields are false
  const shouldDisplay = isAaMastered && !isAtlassianLogin;
  const isAllPersonalDataAvailable = id && me && name && aaId && email;
  const isAaOrgDataAvailable = orgName && aaEmail && aaLogin && aaLoginId;

  const closeOverlayImplicitly = useCallback(() => {
    setOverlayOpen(false);
    Analytics.sendClosedComponentEvent({
      attributes: analyticsContext,
      containers: analyticsContainers,
      source: analyticsSource,
      componentType: 'overlay',
      componentName: 'atlassianAccountOnboardingOverlay',
    });
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
  }, []);

  const onShowAutomaticMigrationConfirmation = useCallback(() => {
    setScreen(ScreenType.AUTOMATIC_MIGRATION_CONFIRMATION);
    setOverlayOpen(true);
  }, []);

  const onShowAtlassianProfile = useCallback(() => {
    setScreen(ScreenType.ATLASSIAN_PROFILE);
    setOverlayOpen(true);
  }, []);

  const onShowConfirmation = useCallback(() => {
    setScreen(ScreenType.ACCOUNT_CONFIRMATION);
    setOverlayOpen(true);
  }, []);

  const onShowAccountUpdate = useCallback(() => {
    setScreen(ScreenType.UPDATE_ACCOUNT);
    setOverlayOpen(true);
    Analytics.sendScreenEvent({
      attributes: analyticsContext,
      containers: analyticsContainers,
      name: ScreenType.UPDATE_ACCOUNT,
    });
  }, []);

  const onShowEmailMismatch = useCallback(() => {
    setScreen(ScreenType.EMAIL_MISMATCH);
    setOverlayOpen(true);
  }, []);

  const onDisplayBanner = useCallback(() => {
    if (!sentInitialAnalytics) {
      Analytics.sendViewedBannerEvent({
        attributes: analyticsContext,
        containers: analyticsContainers,
        source: 'atlassianAccountAutomaticOnboarding',
        bannerName: analyticsSource,
      });
      setSentInitalAnalytics(true);
    }
  }, [sentInitialAnalytics]);

  const onClickUpdateAccountFromBanner = () => {
    onShowAccountUpdate();
    Analytics.sendClickedButtonEvent({
      attributes: analyticsContext,
      containers: analyticsContainers,
      buttonName: 'atlassianAutomaticOnboardingUpdateAccountButton',
      source: analyticsSource,
    });
  };

  useEffect(() => {
    new Image().src = LeftWaveImageUrl;
    new Image().src = RightWaveImageUrl;

    if (shouldShowAutomaticMigrationConfirmationOverlay) {
      onShowAutomaticMigrationConfirmation();
    } else if (shouldShowEmailMismatchOverlay) {
      onShowEmailMismatch();
      onDisplayBanner();
    } else if (
      shouldShowAtlassianProfile ||
      needsAtlassianAccountRelinkedConfirmation(oneTimeMessagesDismissed)
    ) {
      onShowAtlassianProfile();
      onDisplayBanner();
    } else if (shouldShowSuccessOverlay) {
      onShowConfirmation();
    } else if (
      shouldDisplay &&
      isAllPersonalDataAvailable &&
      isAaOrgDataAvailable &&
      !sentInitialAnalytics
    ) {
      onShowAccountUpdate();
      onDisplayBanner();
    }
  }, [
    aaEmail,
    aaId,
    aaLogin,
    aaLoginId,
    oneTimeMessagesDismissed,
    shouldDisplay,
    isAaOrgDataAvailable,
    isAllPersonalDataAvailable,
    sentInitialAnalytics,
    onDisplayBanner,
    onShowAccountUpdate,
    onShowEmailMismatch,
    onShowAutomaticMigrationConfirmation,
    onShowAtlassianProfile,
    onShowConfirmation,
  ]);

  // Ensure we have all the data necessary to render (none of our fields are null)
  if (!(id && me && name && aaId && email)) {
    return null;
  }

  analyticsContext = {
    enterpriseId: ent?.id,
    emailCount: me.logins.length,
  };

  analyticsContainers = {
    enterpriseId: ent?.id,
  };

  // The success overlay should be returned without the banner
  if (forceShowOnboardingOverlay(oneTimeMessagesDismissed)) {
    return isOverlayOpen ? (
      <Overlay
        background={OverlayBackground.LIGHT}
        onClose={closeOverlayImplicitly}
        alignment={OverlayAlignment.TOP}
      >
        <AtlassianAutomaticOnboardingOverlay
          id={id}
          name={name}
          aaEmail={email}
          logins={me && me.logins}
          oneTimeMessagesDismissed={oneTimeMessagesDismissed}
          orgName={orgName}
          dismiss={closeOverlay}
          credentialsRemovedCount={credentialsRemovedCount}
          screen={screen}
          setScreen={setScreen}
          analyticsContext={analyticsContext}
          analyticsContainers={analyticsContainers}
        />
      </Overlay>
    ) : null;
  }

  if (!shouldDisplay || !(orgName && aaEmail && aaLogin && aaLoginId)) {
    return null;
  }

  return (
    <div>
      <Banner
        bannerColor={N0}
        className={styles.banner}
        shadow
        roundedCorners
        alignCloseTop
      >
        <div className={styles.container}>
          <div className={styles.body}>
            <div className={styles.automaticOnboardingMsg}>
              <div>
                <span>
                  <b>{format('heads up')}</b> {format('update your account')}
                  {'.'}
                </span>
              </div>
              <div className={styles.controls}>
                <PrimaryButton
                  size={ButtonSizes.WIDE}
                  onClick={onClickUpdateAccountFromBanner}
                >
                  {format('update')}
                </PrimaryButton>
              </div>
            </div>
            {isOverlayOpen && (
              <Overlay
                background={OverlayBackground.LIGHT}
                onClose={closeOverlayImplicitly}
                alignment={OverlayAlignment.TOP}
              >
                <AtlassianAutomaticOnboardingOverlay
                  id={id}
                  name={name}
                  aaEmail={aaEmail || ''}
                  logins={me && me.logins}
                  oneTimeMessagesDismissed={oneTimeMessagesDismissed}
                  orgName={orgName}
                  dismiss={closeOverlay}
                  credentialsRemovedCount={credentialsRemovedCount}
                  screen={screen}
                  setScreen={setScreen}
                  analyticsContext={analyticsContext}
                  analyticsContainers={analyticsContainers}
                />
              </Overlay>
            )}
          </div>
        </div>
        <img
          alt=""
          src={LeftWaveImageUrl}
          className={classNames(styles.waveImg, styles.leftWaveImg)}
        />
        <img
          alt=""
          src={RightWaveImageUrl}
          className={classNames(styles.waveImg, styles.rightWaveImg)}
        />
      </Banner>
    </div>
  );
};
